/**
 * The /demo showcase clock (spec 2026-09-29 A4; Alan's reviews 2026-09-29).
 * Pure: given the elapsed time and what the server has answered so far, which
 * stage is on, how far the progress bar is, may the price show?
 *
 *  - the price never appears before the showcase has run, and never before
 *    the quote exists;
 *  - a stage only prints a number it actually has;
 *  - stages overlap by CROSSFADE_MS — no hard cuts.
 *
 * @module web/screens/demo/showcase
 */

export type StageKey = "scan" | "measure" | "match" | "guard";

export interface StageSpan {
  key: StageKey;
  start: number;
  end: number;
}

/** Reference timeline, ms. Match gets the most time: spin, lock on, qualify, rank. */
export const TIMELINE: StageSpan[] = [
  { key: "scan", start: 0, end: 2600 },
  { key: "measure", start: 2600, end: 5400 },
  { key: "match", start: 5400, end: 11800 },
  // Long enough that the green check stands ~0.7 s before the price (Alan 2026-10-01).
  { key: "guard", start: 11800, end: 15000 },
];
export const SHOWCASE_MS = 15400;
/** Quiet departure from the silver-service intake into the showcase. */
export const LAUNCH_MS = 500;
/** Meadow: the button answers ("Pasted · N words"), the hook slip rises, then the page lifts away. The quote is already in flight. */
export const MEADOW_LAUNCH_MS = 1100;
/** How long two stages overlap while one fades out and the next fades in. */
export const CROSSFADE_MS = 800;

/** Reduce Motion: same stages, 900 ms each, crossfades only. */
export const REDUCED_TIMELINE: StageSpan[] = TIMELINE.map((s, i) => ({ key: s.key, start: i * 900, end: (i + 1) * 900 }));
export const REDUCED_SHOWCASE_MS = REDUCED_TIMELINE.length * 900 + 200;

export interface ClockState {
  stage: StageKey;
  /** 0..1 inside the current stage (1 while holding). */
  progress: number;
  /** Showcase is over but the quote is not: Guard loops calmly. */
  holding: boolean;
  /** Hand over to the price card. */
  reveal: boolean;
  /** 0..1 for the top bar. Stops short of full until the quote is really there. */
  bar: number;
  /** Whole seconds left on the reference clock, for "Price in ~Ns". */
  secondsLeft: number;
  /** 0..1 over the whole showcase — drives the brief's slow climb out of view. */
  overall: number;
}

export const BAR_CEILING_WITHOUT_QUOTE = 0.92;

export function clock(elapsedMs: number, quoteReady: boolean, reduced = false): ClockState {
  const line = reduced ? REDUCED_TIMELINE : TIMELINE;
  const total = reduced ? REDUCED_SHOWCASE_MS : SHOWCASE_MS;
  const raw = Math.min(1, elapsedMs / total);
  const bar = quoteReady ? raw : Math.min(raw, BAR_CEILING_WITHOUT_QUOTE);
  const secondsLeft = Math.max(0, Math.ceil((total - elapsedMs) / 1000));
  if (elapsedMs >= total) {
    return quoteReady
      ? { stage: "guard", progress: 1, holding: false, reveal: true, bar: 1, secondsLeft: 0, overall: 1 }
      : { stage: "guard", progress: 1, holding: true, reveal: false, bar, secondsLeft: 0, overall: 1 };
  }
  const span = line.find((s) => elapsedMs < s.end) ?? line[line.length - 1]!;
  const progress = Math.min(1, Math.max(0, (elapsedMs - span.start) / (span.end - span.start)));
  return { stage: span.key, progress, holding: false, reveal: false, bar, secondsLeft, overall: raw };
}

export type MatchPhase = "spin" | "qualify" | "rank";

/**
 * Match stage beats (Alan 2026-10-01): everyone arrives as a wave, then a hand
 * narrows a slider filter by filter (the drops happen as the knob arrives), then
 * the top three are ranked.
 */
export const WAVE_END = 0.18;
export const SLIDER_END = 0.66;
export function matchPhase(progress: number): MatchPhase {
  if (progress < WAVE_END) return "spin";
  if (progress < SLIDER_END) return "qualify";
  return "rank";
}

const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
/** Where the range ends once every filter is in: a narrow window in the middle. */
export const RANGE_FINAL = { lo: 0.36, hi: 0.64 };

export interface RangeState {
  /** Left and right knob, 0..1. */
  lo: number;
  hi: number;
  /** Where the hand's fingertip is on the rail, 0..1. */
  hand: number;
  pressing: boolean;
  /** How many filters are in force. */
  applied: number;
}

/**
 * The range slider (Alan 2026-10-01: "eingrenzen" — narrowed from both sides).
 * Motions alternate left knob → right knob; each motion = the hand glides to the
 * knob (25 %), presses and pulls it inwards (50 %), lets go and rests (25 %).
 * A filter counts once its pull has landed. One filter still gets two pulls,
 * so the window always closes from both sides.
 */
export function rangeAt(progress: number, filters: number): RangeState {
  const n = Math.max(1, filters), m = Math.max(2, n);
  const nl = Math.ceil(m / 2), nr = Math.floor(m / 2);
  const t = Math.min(1, Math.max(0, (progress - WAVE_END) / (SLIDER_END - WAVE_END)));
  const loAfter = (k: number) => RANGE_FINAL.lo * (k / nl);
  const hiAfter = (k: number) => 1 - (1 - RANGE_FINAL.hi) * (k / nr);
  // Knob positions after `done` complete motions (motion 0 = left, 1 = right, …).
  const knobs = (done: number) => ({ lo: loAfter(Math.ceil(done / 2)), hi: hiAfter(Math.floor(done / 2)) });
  const appliedAfter = (done: number) => Math.ceil((done * n) / m);
  if (t >= 1) return { ...knobs(m), hand: RANGE_FINAL.hi, pressing: false, applied: n };
  const i = Math.min(m - 1, Math.floor(t * m)), f = t * m - i;
  const before = knobs(i), after = knobs(i + 1), left = i % 2 === 0;
  const from = left ? before.lo : before.hi, to = left ? after.lo : after.hi;
  const prevHand = i === 0 ? from : (i % 2 === 1 ? before.lo : before.hi);
  if (f < 0.25) return { ...before, hand: prevHand + (from - prevHand) * easeInOut(f / 0.25), pressing: false, applied: appliedAfter(i) };
  if (f < 0.75) {
    const at = from + (to - from) * easeInOut((f - 0.25) / 0.5);
    return { ...(left ? { lo: at, hi: before.hi } : { lo: before.lo, hi: at }), hand: at, pressing: true, applied: appliedAfter(i) };
  }
  return { ...after, hand: to, pressing: false, applied: appliedAfter(i + 1) };
}

/** Ease-out count-up, shared by every ticker so they feel like one instrument. */
export function countUp(target: number, progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return Math.round(target * (1 - Math.pow(1 - p, 3)));
}

export interface ChainStage {
  key: "network" | "niche" | "language" | "trained" | "available" | "ranked";
  count: number;
  keptIds: string[];
}

export type AiEffort = "light" | "medium" | "heavy";

/** Illustrative scene density only; never used for pricing or real generation counts. */
export function productionPreview(brief: string, quotedEffort: AiEffort | null) {
  const concept = /\banimat(?:ed|ion|ionen)?\b/i.test(brief) ? "Animated"
    : /\bugc\b|user.generated|creator ad/i.test(brief) ? "UGC"
    : /\bvsl\b|video sales letter/i.test(brief) ? "VSL" : null;
  const effort: AiEffort | null = concept === "Animated" ? "heavy" : concept === "UGC" ? "medium" : concept === "VSL" ? "light" : quotedEffort;
  return { concept, effort, count: effort === "heavy" ? 9 : effort === "medium" ? 6 : 3 };
}

export interface QuoteView {
  ai: AiEffort | null;
}

/** The one status line per stage — short, and only numbers we have. */
export function stageLine(
  stage: StageKey,
  quote: QuoteView | null,
  match: { phase: MatchPhase; total: number; qualified: number } | null,
  holding: boolean,
): string {
  if (holding) return "Almost there";
  switch (stage) {
    case "scan":
      return "Reading your brief";
    case "measure":
      return quote?.ai ? "How much AI work your video needs" : "Measuring the script";
    case "match":
      if (!match) return "Checking our network";
      if (match.phase === "spin") return `Scanning ${match.total} editors`;
      if (match.phase === "qualify") return "We take our time picking who cuts your ad";
      return "Your top 3: skill first, then availability";
    case "guard":
      return "We send it back until it's perfect. You only get the final.";
  }
}
